/**
 * YouTube Data API Service
 *
 * Provides methods to interact with YouTube Data API v3
 * - Fetch user's playlists
 * - Fetch playlist items (videos)
 * - Search for music
 * - Fetch liked videos
 */

import { youtubeAuth } from './YouTubeAuth';

const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3';

class YouTubeAPIService {
  /**
   * Make authenticated API request to YouTube
   */
  async apiRequest(endpoint, params = {}) {
    const token = youtubeAuth.getAccessToken();

    if (!token) {
      throw new Error('Not authenticated with YouTube');
    }

    const url = new URL(`${YOUTUBE_API_BASE}/${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        youtubeAuth.clearToken();
        throw new Error('YouTube session expired');
      }
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.error?.message || `YouTube API error: ${response.status}`
      );
    }

    return response.json();
  }

  /**
   * Get user's playlists
   */
  async getMyPlaylists(pageToken = null, maxResults = 25) {
    const data = await this.apiRequest('playlists', {
      part: 'snippet,contentDetails',
      mine: true,
      maxResults,
      pageToken,
    });

    return {
      items:
        data.items?.map((item) => ({
          id: item.id,
          title: item.snippet.title,
          description: item.snippet.description,
          thumbnail:
            item.snippet.thumbnails?.medium?.url ||
            item.snippet.thumbnails?.default?.url,
          itemCount: item.contentDetails?.itemCount || 0,
          publishedAt: item.snippet.publishedAt,
          source: 'youtube',
        })) || [],
      nextPageToken: data.nextPageToken,
      totalResults: data.pageInfo?.totalResults || 0,
    };
  }

  /**
   * Get items (videos) from a playlist
   */
  async getPlaylistItems(playlistId, pageToken = null, maxResults = 50) {
    const data = await this.apiRequest('playlistItems', {
      part: 'snippet,contentDetails',
      playlistId,
      maxResults,
      pageToken,
    });

    return {
      items:
        data.items
          ?.map((item) => ({
            id: item.contentDetails.videoId,
            playlistItemId: item.id,
            title: item.snippet.title,
            artist:
              item.snippet.videoOwnerChannelTitle?.replace(' - Topic', '') ||
              'Unknown Artist',
            thumbnail:
              item.snippet.thumbnails?.medium?.url ||
              item.snippet.thumbnails?.default?.url,
            description: item.snippet.description,
            position: item.snippet.position,
            publishedAt: item.snippet.publishedAt,
            source: 'youtube',
            type: 'video',
          }))
          .filter(
            (item) =>
              item.title !== 'Deleted video' && item.title !== 'Private video'
          ) || [],
      nextPageToken: data.nextPageToken,
      totalResults: data.pageInfo?.totalResults || 0,
    };
  }

  /**
   * Get user's liked videos playlist
   */
  async getLikedVideos(pageToken = null, maxResults = 50) {
    // The liked videos playlist ID is 'LL' for the authenticated user
    return this.getPlaylistItems('LL', pageToken, maxResults);
  }

  /**
   * Get user's "Watch Later" playlist
   */
  async getWatchLater(pageToken = null, maxResults = 50) {
    // Watch Later playlist ID is 'WL' for the authenticated user
    return this.getPlaylistItems('WL', pageToken, maxResults);
  }

  /**
   * Search for videos (music)
   */
  async search(query, pageToken = null, maxResults = 25) {
    const data = await this.apiRequest('search', {
      part: 'snippet',
      q: query,
      type: 'video',
      videoCategoryId: '10', // Music category
      maxResults,
      pageToken,
    });

    return {
      items:
        data.items?.map((item) => ({
          id: item.id.videoId,
          title: item.snippet.title,
          artist:
            item.snippet.channelTitle?.replace(' - Topic', '') ||
            'Unknown Artist',
          thumbnail:
            item.snippet.thumbnails?.medium?.url ||
            item.snippet.thumbnails?.default?.url,
          description: item.snippet.description,
          publishedAt: item.snippet.publishedAt,
          source: 'youtube',
          type: 'video',
        })) || [],
      nextPageToken: data.nextPageToken,
      totalResults: data.pageInfo?.totalResults || 0,
    };
  }

  /**
   * Search for movies and TV shows on YouTube
   * Uses broader search without music category restriction
   */
  async searchMoviesAndTV(query, pageToken = null, maxResults = 25) {
    const data = await this.apiRequest('search', {
      part: 'snippet',
      q: `${query} movie OR TV show OR full episode`,
      type: 'video',
      maxResults,
      pageToken,
      order: 'relevance',
    });

    return {
      items:
        data.items?.map((item) => ({
          id: item.id.videoId,
          title: item.snippet.title,
          artist: item.snippet.channelTitle || 'Unknown',
          thumbnail:
            item.snippet.thumbnails?.medium?.url ||
            item.snippet.thumbnails?.default?.url,
          description: item.snippet.description,
          publishedAt: item.snippet.publishedAt,
          source: 'youtube',
          type: 'video',
          contentType: 'movie-tv', // Distinguish from music videos
        })) || [],
      nextPageToken: data.nextPageToken,
      totalResults: data.pageInfo?.totalResults || 0,
    };
  }

  /**
   * Search for sports content on YouTube (highlights, full games, analysis)
   */
  async searchSports(query, league = null, pageToken = null, maxResults = 25) {
    let searchQuery = query;
    if (league) {
      searchQuery = `${query} ${league} highlights OR full game OR analysis`;
    } else {
      searchQuery = `${query} sports highlights OR full game`;
    }

    const data = await this.apiRequest('search', {
      part: 'snippet',
      q: searchQuery,
      type: 'video',
      maxResults,
      pageToken,
      order: 'relevance',
    });

    return {
      items:
        data.items?.map((item) => ({
          id: item.id.videoId,
          title: item.snippet.title,
          artist: item.snippet.channelTitle || 'Unknown',
          thumbnail:
            item.snippet.thumbnails?.medium?.url ||
            item.snippet.thumbnails?.default?.url,
          description: item.snippet.description,
          publishedAt: item.snippet.publishedAt,
          source: 'youtube',
          type: 'video',
          contentType: 'sports', // Distinguish from music/movies
          league: league || null,
        })) || [],
      nextPageToken: data.nextPageToken,
      totalResults: data.pageInfo?.totalResults || 0,
    };
  }

  /**
   * Get video details (for duration, statistics, etc.)
   */
  async getVideoDetails(videoIds) {
    if (!videoIds || videoIds.length === 0) return [];

    const ids = Array.isArray(videoIds) ? videoIds.join(',') : videoIds;

    const data = await this.apiRequest('videos', {
      part: 'snippet,contentDetails,statistics',
      id: ids,
    });

    return (
      data.items?.map((item) => ({
        id: item.id,
        title: item.snippet.title,
        artist:
          item.snippet.channelTitle?.replace(' - Topic', '') ||
          'Unknown Artist',
        thumbnail: item.snippet.thumbnails?.medium?.url,
        duration: this.parseDuration(item.contentDetails?.duration),
        viewCount: parseInt(item.statistics?.viewCount || 0),
        likeCount: parseInt(item.statistics?.likeCount || 0),
        source: 'youtube',
        type: 'video',
      })) || []
    );
  }

  /**
   * Parse ISO 8601 duration to seconds
   */
  parseDuration(isoDuration) {
    if (!isoDuration) return 0;

    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;

    const hours = parseInt(match[1] || 0);
    const minutes = parseInt(match[2] || 0);
    const seconds = parseInt(match[3] || 0);

    return hours * 3600 + minutes * 60 + seconds;
  }

  /**
   * Format duration from seconds to mm:ss or hh:mm:ss
   */
  formatDuration(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00';

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Get all music-related content from user's account
   * Combines playlists, liked videos for a unified view
   */
  async getMyMusicLibrary() {
    const [playlists, likedVideos] = await Promise.all([
      this.getMyPlaylists(),
      this.getLikedVideos(null, 10), // Just preview
    ]);

    return {
      playlists: playlists.items,
      likedVideos: likedVideos.items,
      totalPlaylists: playlists.totalResults,
      totalLikedVideos: likedVideos.totalResults,
    };
  }
}

// Export singleton instance
export const youtubeAPI = new YouTubeAPIService();
export default youtubeAPI;
